// src/hooks/useLocalStorage.js

import { useState, useEffect } from 'react';

const VERSION_KEY = '__lsVersion';

/**
 * Resolve a persisted value from its raw localStorage string.
 *
 * Pure (no DOM access) so it can be unit-tested. Returns `defaultValue` for any
 * problem — missing data, unparseable JSON, a version mismatch, or a failed
 * validate — and never throws.
 *
 * @param {string|null} raw  Result of localStorage.getItem(key).
 * @param {*} defaultValue
 * @param {{version?: number, validate?: (v:any)=>boolean, migrate?: (v:any, oldVersion:any)=>any}} [options]
 */
export function readPersisted(raw, defaultValue, options = {}) {
  const { version, validate, migrate } = options;
  const versioned = version !== undefined;

  if (raw === null || raw === undefined) return defaultValue;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return defaultValue;
  }

  let candidate;
  if (versioned) {
    const isWrapped = parsed && typeof parsed === 'object' && VERSION_KEY in parsed;
    const storedVersion = isWrapped ? parsed[VERSION_KEY] : undefined;
    // A legacy (pre-versioning) entry is the raw value itself, with no wrapper.
    const storedValue = isWrapped ? parsed.value : parsed;

    if (storedVersion === version) {
      candidate = storedValue;
    } else if (migrate) {
      // Older (or unversioned) data — let the caller upgrade it.
      try {
        candidate = migrate(storedValue, storedVersion);
      } catch {
        return defaultValue;
      }
      if (candidate === undefined) return defaultValue;
    } else {
      // Shape may have changed across this version — discard stale data.
      return defaultValue;
    }
  } else {
    candidate = parsed;
  }

  try {
    if (validate && !validate(candidate)) return defaultValue;
  } catch {
    return defaultValue;
  }
  return candidate;
}

/** Serialize a value for storage, tagging it with the version when one is set. */
export function serializePersisted(value, options = {}) {
  const { version } = options;
  const payload = version !== undefined ? { [VERSION_KEY]: version, value } : value;
  return JSON.stringify(payload);
}

/**
 * Persist React state to localStorage, surviving reloads.
 *
 * Backward compatible: `useLocalStorage(key, default)` behaves exactly as before
 * (raw JSON, no guards). The optional third argument protects against stale data
 * whose *shape* changed across deploys — the classic "old browser, new code" bug:
 *
 *   options.version  - integer tag stored alongside the value. On load, data
 *                      written under a different version (or none) is discarded
 *                      (or handed to options.migrate). Bump it whenever you change
 *                      the shape of the stored value.
 *   options.validate - (value) => boolean. Loaded value is discarded if this
 *                      returns false. Use for a quick shape sanity check.
 *   options.migrate  - (oldValue, oldVersion) => newValue. Upgrade older data in
 *                      place; return undefined to fall back to the default.
 *
 * NOTE: apply a version to a key the next time you change its shape, not
 * retroactively — turning it on discards whatever is already stored under that
 * key (which for keys like `tournamentBracket` is a user's in-progress data).
 */
export function useLocalStorage(key, defaultValue, options = {}) {
  const { version } = options;

  const [value, setValue] = useState(() => {
    try {
      return readPersisted(localStorage.getItem(key), defaultValue, options);
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, serializePersisted(value, options));
    } catch (err) {
      console.error(`Error saving ${key} to localStorage:`, err);
    }
    // Only `version` affects what gets written; keeping deps minimal avoids an
    // inline `options` object churning the effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, value, version]);

  return [value, setValue];
}
